import type { OrderEventType, OrderStatus } from "./enums.js";
import type { FulfillmentPath } from "./products.js";

// Event-sourced fulfillment (issue #243). The operator ticks physical-production
// steps on the admin order-detail screen; each tick/un-tick is appended to the
// append-only `order_events` log with an `actor`, and the coarse
// customer-facing `order_status` is a PROJECTION of that log — never mutated
// directly. This module is the pure, IO-free core: the registry of steps, the
// projection that reads the log, and the resolver that turns an operator action
// into the event to append (+ any status change). No DB, no Zod, no clock.
//
// Two design rules make un-ticking safe and the log the single source of truth:
//   • last-event-wins — un-ticking is a NEW `{done:false}` event, not a delete,
//     so the full history (who/when) survives in the timeline.
//   • status is DERIVED — `currentStatus` is the highest-status step that is
//     currently done, so un-ticking a mapped step regresses the status too
//     (e.g. un-tick `handed_to_carrier` drops `shipped` → `in_production`).

// The coarse status lifecycle, in ascending order. `currentStatus` is the
// mapped status of the furthest-along done step by this ranking. `refunded` /
// `cancelled` are terminal sidetracks no fulfillment step reaches, so they are
// intentionally absent (a future refund/cancel branch owns them) — a step that
// somehow mapped to one would rank -1 and never win.
const LIFECYCLE_ORDER: readonly OrderStatus[] = [
  "pending",
  "paid",
  "in_production",
  "shipped",
  "delivered",
];

// Where a fulfillment step lives in a MIXED order (ADR-0054 §5, #408). Everything
// converges at ONE parcel, so logistics + the auto lifecycle stay `order`-level;
// physical production is per `order_lines` row, so those steps are `line`-scoped and
// their `order_events` carry an `order_line_id`. The order-level coarse status only
// advances through a line-scoped mapped step once EVERY line has reached it.
export type FulfillmentScope = "order" | "line";

// The per-line, per-TYPE production task a line surfaces (ADR-0054 §5, #408): a
// `producer_packet` for a producer-made line (the pillow → InPost factory), a `print`
// task for an operator-printed line (the 3D print). Which line-scoped steps apply is
// keyed off this — a producer packet carries the factory handoff steps a self-print
// has no analogue for. Derived from the Product type's `fulfillment_path`.
export const PRODUCTION_TASK_KINDS = ["producer_packet", "print"] as const;
export type ProductionTaskKind = (typeof PRODUCTION_TASK_KINDS)[number];

// Map a Product type's fulfillment path onto its per-line production task kind
// (ADR-0054 §5): `producer` → a producer packet (files → factory → back → QC),
// `self` → an in-house print task. The single seam so admin + api never disagree on
// which task a line surfaces.
export function taskKindForFulfillmentPath(path: FulfillmentPath): ProductionTaskKind {
  return path === "producer" ? "producer_packet" : "print";
}

// One entry of the fulfillment checklist. `key` doubles as the `order_events`
// event type (a member of ORDER_EVENT_TYPES). `advancesTo` maps a step onto the
// coarse status it represents; steps without it (e.g. `packed`) never move the
// status. `trigger` distinguishes the two auto steps the app already emits
// (checkout + Stripe webhook) from the manual steps the operator ticks — auto
// steps render read-only and reject manual tick/un-tick. `scope` (ADR-0054 §5, #408)
// splits the order-level lifecycle/logistics steps from the per-line production
// steps; a line-scoped step declares the `taskKinds` (producer packet / print) it
// belongs to, so a line only surfaces the steps its type actually needs.
export interface FulfillmentStep {
  key: OrderEventType;
  label: string;
  advancesTo?: OrderStatus;
  trigger: "auto" | "manual";
  scope: FulfillmentScope;
  // Line-scoped steps only: which production task kinds include this step. A
  // producer packet carries the factory handoff (`files_sent_to_factory`,
  // `received_from_factory`) a self-print does not. Absent on order-scoped steps.
  taskKinds?: readonly ProductionTaskKind[];
  // The buyer lifecycle email fired best-effort when the operator ticks this step
  // (#315) — the two mapped steps only. Driven off the registry so route + sender
  // never re-encode the step key.
  lifecycleEmail?: "shipped" | "delivered";
  // This step registers shipment tracking as it's ticked (#315): the admin surfaces
  // a parcel-number capture, the tick persists it onto `orders`, and the shipped
  // email reads it. One step carries it (`handed_to_carrier`).
  capturesTracking?: true;
}

// The initial self-fulfilment registry (issue #243 table). Ordered = checklist
// order. Adding a step (or a future exception branch like `qc_failed`) is a new
// entry here + a literal in ORDER_EVENT_TYPES — no enum change, no migration.
export const FULFILLMENT_STEPS: readonly FulfillmentStep[] = [
  {
    key: "order_created",
    label: "Zamówienie utworzone",
    advancesTo: "pending",
    trigger: "auto",
    scope: "order",
  },
  {
    key: "order_paid",
    label: "Zamówienie opłacone",
    advancesTo: "paid",
    trigger: "auto",
    scope: "order",
  },
  {
    key: "production_files_generated",
    label: "Pliki produkcyjne wygenerowane",
    trigger: "manual",
    scope: "line",
    taskKinds: ["producer_packet"],
  },
  {
    key: "files_sent_to_factory",
    label: "Pliki wysłane do producenta",
    trigger: "manual",
    scope: "line",
    taskKinds: ["producer_packet"],
  },
  {
    key: "production_started",
    label: "Produkcja rozpoczęta",
    advancesTo: "in_production",
    trigger: "manual",
    scope: "line",
    taskKinds: ["producer_packet", "print"],
  },
  {
    key: "production_finished",
    label: "Produkcja zakończona",
    trigger: "manual",
    scope: "line",
    taskKinds: ["producer_packet", "print"],
  },
  {
    key: "received_from_factory",
    label: "Odebrane od producenta",
    trigger: "manual",
    scope: "line",
    taskKinds: ["producer_packet"],
  },
  {
    key: "qc_passed",
    label: "Kontrola jakości zdana",
    trigger: "manual",
    scope: "line",
    taskKinds: ["producer_packet", "print"],
  },
  { key: "packed", label: "Spakowane", trigger: "manual", scope: "order" },
  {
    key: "handed_to_carrier",
    label: "Przekazane kurierowi",
    advancesTo: "shipped",
    trigger: "manual",
    scope: "order",
    lifecycleEmail: "shipped",
    capturesTracking: true,
  },
  {
    key: "delivered",
    label: "Dostarczone",
    advancesTo: "delivered",
    trigger: "manual",
    scope: "order",
    lifecycleEmail: "delivered",
  },
];

// Step keys in checklist order — the projection + the SPA checklist iterate it.
export const FULFILLMENT_STEP_KEYS: readonly OrderEventType[] = FULFILLMENT_STEPS.map((s) => s.key);

// The minimal shape the pure core needs from an `order_events` row. The API maps
// each row (`createdAt.toISOString()` → `at`, `actor` coalesced later) onto this.
// `at` is echoed straight to the wire; ordering is by array position (the repo
// returns events chronologically), so the core never parses timestamps.
export interface FulfillmentEvent {
  eventType: string;
  at: string;
  actor?: string | null;
  payload?: unknown;
  // The `order_events.order_line_id` a PRODUCTION step carries (ADR-0054 §5, #408).
  // Null/absent for order-level events (the auto lifecycle + logistics steps, and
  // every legacy pre-#408 event) — those apply to the whole order. A line-scoped
  // step tagged with a line applies only to that line.
  orderLineId?: string | null;
}

// One projected step: its current done-state plus who/when completed it. `at` /
// `actor` are non-null exactly when `done` — an un-ticked step carries no
// completion (its un-tick still lives in the raw event timeline). `actor`
// coalesces a null column (auto/legacy events) to "system".
export interface FulfillmentStepState {
  key: OrderEventType;
  done: boolean;
  at: string | null;
  actor: string | null;
}

export interface FulfillmentProjection {
  steps: FulfillmentStepState[];
  currentStatus: OrderStatus;
}

// One line of a mixed order as the fulfillment projection needs to see it (ADR-0054
// §5, #408): its `order_lines.id` plus the production `taskKind` derived from its
// Product type's fulfillment path. The caller (the api service) resolves the kind via
// `taskKindForFulfillmentPath` — the projection stays IO-free.
export interface FulfillmentLineInput {
  orderLineId: string;
  taskKind: ProductionTaskKind;
}

// The projected per-line production task (ADR-0054 §5, #408): the line, its task kind,
// the line-scoped steps that task kind includes (in registry order, each with its
// last-event-wins done-state for THIS line), and whether the line's production is
// complete (every applicable step done — the gate the order-level status advances on).
export interface FulfillmentLineTask {
  orderLineId: string;
  taskKind: ProductionTaskKind;
  steps: FulfillmentStepState[];
  done: boolean;
}

// The full mixed-order fulfillment projection (ADR-0054 §5, #408): the order-level
// ladder (`orderSteps` — the auto lifecycle + logistics steps, order-scoped), the
// per-line production tasks (`lines`), and the derived coarse `currentStatus`. ONE
// projection: the admin order detail renders both parts and the customer Order view
// reads the same derived status, so operator and buyer can never disagree.
export interface OrderFulfillmentProjection {
  orderSteps: FulfillmentStepState[];
  lines: FulfillmentLineTask[];
  currentStatus: OrderStatus;
}

export type StepAction = "tick" | "untick";

// Which line a step action targets + the order's lines, for the coarse-status
// re-derivation (ADR-0054 §5, #408). A line-scoped step tick carries `orderLineId`
// (the appended event is tagged with it) and `lines` (so the gate can check whether
// EVERY line has now reached the mapped step). Order-scoped steps omit `orderLineId`;
// omitting the whole context reproduces the pre-#408 order-level behavior.
export interface StepActionContext {
  orderLineId?: string;
  lines?: readonly FulfillmentLineInput[];
}

export interface StepActionResult {
  eventToAppend: {
    eventType: OrderEventType;
    actor: string;
    payload: { done: boolean };
    orderLineId?: string;
  };
  statusChange?: OrderStatus;
}

// A step is done unless its latest event explicitly carries `{ done: false }`
// (an un-tick). Bare events (auto steps, legacy rows, or an explicit
// `{ done: true }`) count as done — so the projection is backward-compatible
// with events written before the checklist existed. `undefined` = no event yet.
function eventIsDone(event: FulfillmentEvent | undefined): boolean {
  if (event === undefined) return false;
  const payload = event.payload;
  const untick =
    typeof payload === "object" &&
    payload !== null &&
    (payload as Record<string, unknown>).done === false;
  return !untick;
}

// Composite key for the per-(type, line) latest-event map. ` ` can't appear in a
// uuid or an event type, so it's a safe separator.
function lineKey(eventType: string, orderLineId: string): string {
  return `${eventType} ${orderLineId}`;
}

// Latest event per lookup key (last-event-wins): events arrive chronologically, so a
// plain overwrite while iterating leaves each key mapped to its newest event. Three
// indexes off ONE pass (ADR-0054 §5, #408):
//   • byTypeAll        — newest event per type across ALL events (the pre-#408 flat
//                        view; the no-lines `projectFulfillment` path reads this).
//   • byTypeUntagged   — newest UNTAGGED (order-level) event per type; order-scoped
//                        steps read this, and it's the per-line fallback for a legacy
//                        untagged production event (applies to the whole order).
//   • byTypeAndLine    — newest event per (type, line) among TAGGED events; a
//                        line-scoped step reads its own line here.
interface EventIndex {
  byTypeAll: Map<string, FulfillmentEvent>;
  byTypeUntagged: Map<string, FulfillmentEvent>;
  byTypeAndLine: Map<string, FulfillmentEvent>;
}

function indexEvents(events: readonly FulfillmentEvent[]): EventIndex {
  const byTypeAll = new Map<string, FulfillmentEvent>();
  const byTypeUntagged = new Map<string, FulfillmentEvent>();
  const byTypeAndLine = new Map<string, FulfillmentEvent>();
  for (const event of events) {
    byTypeAll.set(event.eventType, event);
    if (event.orderLineId == null) byTypeUntagged.set(event.eventType, event);
    else byTypeAndLine.set(lineKey(event.eventType, event.orderLineId), event);
  }
  return { byTypeAll, byTypeUntagged, byTypeAndLine };
}

function toStepState(
  key: OrderEventType,
  event: FulfillmentEvent | undefined,
): FulfillmentStepState {
  const done = eventIsDone(event);
  return {
    key,
    done,
    at: done && event ? event.at : null,
    actor: done && event ? (event.actor ?? "system") : null,
  };
}

// The done-state event for a line-scoped step on ONE line: the line's own tagged event
// wins; failing that, a legacy UNTAGGED event of that type (a pre-#408 order-wide
// production tick applies to every line). A sibling line's tagged event never leaks in.
function lineEvent(
  idx: EventIndex,
  key: OrderEventType,
  orderLineId: string,
): FulfillmentEvent | undefined {
  return idx.byTypeAndLine.get(lineKey(key, orderLineId)) ?? idx.byTypeUntagged.get(key);
}

// Derive the coarse status from the event index (ADR-0054 §5, #408): the mapped status
// of the furthest-along done+mapped step, defaulting to `pending`. Order-scoped mapped
// steps read their order-level event; a line-scoped mapped step counts as done ONLY when
// every line has reached it (so the order advances to `in_production` once ALL lines have
// started production). With no lines, a line-scoped step falls back to the flat view — so
// this reproduces the pre-#408 single-line behavior byte-for-byte.
function deriveCurrentStatus(idx: EventIndex, lines: readonly FulfillmentLineInput[]): OrderStatus {
  let currentStatus: OrderStatus = "pending";
  let bestRank = 0;
  for (const step of FULFILLMENT_STEPS) {
    if (!step.advancesTo) continue;
    let done: boolean;
    if (step.scope === "order") {
      done = eventIsDone(idx.byTypeUntagged.get(step.key));
    } else if (lines.length === 0) {
      done = eventIsDone(idx.byTypeAll.get(step.key));
    } else {
      done = lines.every((line) => eventIsDone(lineEvent(idx, step.key, line.orderLineId)));
    }
    if (!done) continue;
    const rank = LIFECYCLE_ORDER.indexOf(step.advancesTo);
    if (rank > bestRank) {
      bestRank = rank;
      currentStatus = step.advancesTo;
    }
  }
  return currentStatus;
}

// Project the append-only event log onto the FLAT checklist: every registry step with
// its current done-state, and the derived coarse status (the mapped status of the
// furthest-along done step, defaulting to `pending`). This is the pre-#408 order-level
// view (all 11 steps, no per-line split) — kept for the flat callers; the mixed-order
// view is `projectOrderFulfillment`.
export function projectFulfillment(events: readonly FulfillmentEvent[]): FulfillmentProjection {
  const idx = indexEvents(events);
  const steps: FulfillmentStepState[] = FULFILLMENT_STEPS.map((step) =>
    toStepState(step.key, idx.byTypeAll.get(step.key)),
  );
  return { steps, currentStatus: deriveCurrentStatus(idx, []) };
}

// The mixed-order fulfillment projection (ADR-0054 §5, #408) — the ONE projection that
// powers the admin order detail. Splits the log into the order-level ladder (the
// order-scoped auto/logistics steps) and, per line, the production task its type needs
// (the line-scoped steps for its `taskKind`, each with THIS line's done-state). The
// coarse `currentStatus` is gated: a line-scoped mapped step advances the order only
// once EVERY line has reached it. `lines` order mirrors the input order.
export function projectOrderFulfillment(
  events: readonly FulfillmentEvent[],
  lines: readonly FulfillmentLineInput[],
): OrderFulfillmentProjection {
  const idx = indexEvents(events);

  const orderSteps = FULFILLMENT_STEPS.filter((s) => s.scope === "order").map((step) =>
    toStepState(step.key, idx.byTypeUntagged.get(step.key)),
  );

  const lineTasks: FulfillmentLineTask[] = lines.map((line) => {
    const steps = FULFILLMENT_STEPS.filter(
      (s) => s.scope === "line" && s.taskKinds?.includes(line.taskKind),
    ).map((step) => toStepState(step.key, lineEvent(idx, step.key, line.orderLineId)));
    return {
      orderLineId: line.orderLineId,
      taskKind: line.taskKind,
      steps,
      done: steps.every((s) => s.done),
    };
  });

  return { orderSteps, lines: lineTasks, currentStatus: deriveCurrentStatus(idx, lines) };
}

// The coarse, customer-facing status ladder (ADR-0051 Order view): the four
// stops a buyer sees on `/order/:token`, in ascending order. `pending` is the
// pre-ladder state (order created, payment not yet confirmed) and `refunded` /
// `cancelled` are sidetracks the page handles separately — none is a stop here.
export const CUSTOMER_LADDER_STOPS = [
  "paid",
  "in_production",
  "shipped",
  "delivered",
] as const satisfies readonly OrderStatus[];

// One stop of the coarse customer ladder. `done` is rank-monotonic off the
// derived `currentStatus` (so a buyer never sees a "shipped but not in
// production" gap even if the operator ticked steps out of order); `current`
// marks the single furthest-reached stop; `at` is the mapped step's real event
// timestamp when that step itself is done (null otherwise). Zod mirror lives in
// ./customer-order as `CustomerLadderStepSchema` (same pattern as
// AdminFulfillmentStep vs FulfillmentStepState).
export interface CustomerLadderStep {
  status: OrderStatus;
  done: boolean;
  current: boolean;
  at: string | null;
}

export interface CustomerLadderProjection {
  currentStatus: OrderStatus;
  ladder: CustomerLadderStep[];
}

// Roll the append-only event log up to the coarse 4-stop customer ladder. The
// derived `currentStatus` is the SAME `deriveCurrentStatus` the admin console's
// `projectOrderFulfillment` uses — so, given the order's `lines`, the customer's
// status is byte-for-byte the operator's (including the mixed-order gate: the ladder
// only reaches `in_production` once EVERY line has started). Each stop echoes the
// mapped step's real completion timestamp from the flat view; `done`/`current` come
// from the single derived `currentStatus`, never from the raw `orders.status` column.
// `lines` defaults to empty — a single-line / legacy order needs no line context and
// reproduces the pre-#408 ladder exactly.
export function projectCustomerLadder(
  events: readonly FulfillmentEvent[],
  lines: readonly FulfillmentLineInput[] = [],
): CustomerLadderProjection {
  const { steps } = projectFulfillment(events);
  const currentStatus = deriveCurrentStatus(indexEvents(events), lines);
  // Map each ladder status to the fulfillment step that advances to it (exactly
  // one per mapped status: order_paid, production_started, handed_to_carrier,
  // delivered) so the stop can echo that step's real completion timestamp. For the
  // line-scoped `in_production` step this is the flat "any line" timestamp — a coarse
  // when-production-began stamp, deliberately not per-line (the buyer sees one order).
  const stepByStatus = new Map<OrderStatus, FulfillmentStepState>();
  for (const registryStep of FULFILLMENT_STEPS) {
    if (!registryStep.advancesTo) continue;
    const state = steps.find((s) => s.key === registryStep.key);
    if (state) stepByStatus.set(registryStep.advancesTo, state);
  }

  const currentRank = LIFECYCLE_ORDER.indexOf(currentStatus);
  const ladder: CustomerLadderStep[] = CUSTOMER_LADDER_STOPS.map((status) => {
    const state = stepByStatus.get(status);
    const done = LIFECYCLE_ORDER.indexOf(status) <= currentRank;
    return {
      status,
      done,
      current: status === currentStatus,
      // Only show a timestamp for a stop actually reached (rank-monotonic `done`) AND
      // whose mapped step itself fired — so an out-of-order flat tick can't stamp a
      // stop the gated status hasn't reached.
      at: done && state?.done ? state.at : null,
    };
  });

  return { currentStatus, ladder };
}

// Turn one operator action into the event to append plus any status change.
// Pure: the caller (the transactional endpoint) appends `eventToAppend` and
// applies `statusChange` in one transaction. `statusChange` is emitted only when
// the derived status of the resulting log differs from the current one — so
// no-status-mapping steps (and no-op ticks) leave the status untouched, and an
// un-tick that removes the furthest-along mapped step regresses it.
//
// For a line-scoped step (ADR-0054 §5, #408) pass `ctx.orderLineId` (the appended
// event is tagged with it) and `ctx.lines` (so the before/after status is derived
// with the mixed-order gate — the order advances only once EVERY line has reached the
// mapped step). Omit `ctx` for an order-scoped step or to reproduce pre-#408 behavior.
export function resolveStepAction(
  events: readonly FulfillmentEvent[],
  stepKey: string,
  action: StepAction,
  actor: string,
  ctx: StepActionContext = {},
): StepActionResult {
  const step = FULFILLMENT_STEPS.find((s) => s.key === stepKey);
  if (!step) throw new Error(`unknown fulfillment step: ${stepKey}`);
  if (step.trigger === "auto") {
    throw new Error(`fulfillment step ${stepKey} is system-managed and cannot be ticked manually`);
  }

  // A line-scoped step tags its event with the target line; an order-scoped step never
  // carries one (even if a caller passes it defensively).
  const orderLineId = step.scope === "line" ? ctx.orderLineId : undefined;
  const lines = ctx.lines ?? [];
  const payload = { done: action === "tick" };
  const appended: FulfillmentEvent = { eventType: step.key, at: "", actor, payload, orderLineId };
  const before = deriveCurrentStatus(indexEvents(events), lines);
  const after = deriveCurrentStatus(indexEvents([...events, appended]), lines);

  return {
    eventToAppend: { eventType: step.key, actor, payload, ...(orderLineId ? { orderLineId } : {}) },
    statusChange: after === before ? undefined : after,
  };
}
